import { fetchOpenAIText } from "../utils/openaiClient";

export function useTrueFalseGeneratorLogic({
  trueFalseInput, setTrueFalseInput, trueFalseLevel, trueFalseCount, setTrueFalseCount,
  setTrueFalseLoading, generatedTrueFalse, setGeneratedTrueFalse,
  currentTab, activeEditor, onInsertToBoard,
  setShowTrueFalseGenerator, setSelectedTemplate,
}) {
  const handleTrueFalseGenerate = async () => {
    if (!trueFalseInput.trim()) return;

    setTrueFalseLoading(true);
    try {
      const prompt = `Create true/false statements for the following text. Generate exactly ${trueFalseCount} statements that test comprehension of the text content.

CEFR level: ${trueFalseLevel}.
Level requirements (match strictly):
- A1–A2: very simple sentences, high‑frequency vocabulary, concrete topics; statement length 5–12 words; clear true/false distinctions.
- B1: simple/compound sentences, everyday/academic‑lite vocabulary; statement length 8–18 words; some nuanced true/false distinctions.
- B2: more complex syntax, abstract topics allowed; statement length 10–22 words; subtle true/false distinctions requiring careful reading.
- C1–C2: advanced structures/lexis, abstract/academic topics; statement length 12–26 words; sophisticated true/false distinctions.

Statement rules:
- Cover key facts/ideas from the text; avoid obscure trivia.
- Mix of true and false statements (aim for roughly 50/50 split).
- False statements should be plausible but contain clear errors when compared to the text.
- Statements must be clear and unambiguous at the specified CEFR level.
- Each statement MUST include an explanation that starts with an exact quoted fragment (5–25 words) copied verbatim from the text in double quotes ("...") that proves why the statement is true/false, followed by a brief reasoning. Do NOT paraphrase inside the quotes.

CONSTRAINT: The "statements" array MUST contain exactly ${trueFalseCount} items.

IMPORTANT: Return ONLY valid JSON in this exact format:
{
  "title": "True/False Exercise",
  "level": "${trueFalseLevel}",
  "statements": [
    {
      "statement": "Statement text here",
      "isTrue": true,
      "explanation": '"Exact quoted fragment from the text..." — One short sentence explaining how this quote proves the answer.'
    }
  ]
}

Text: ${trueFalseInput}`;



      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 800,
      });

      if (content) {
        let contentStr = content.trim();
        try {
          // Удаляем markdown-блоки, если есть
          if (contentStr.startsWith('```json')) {
            contentStr = contentStr.replace(/^```json\n/, '').replace(/\n```$/, '');
          } else if (contentStr.startsWith('```')) {
            contentStr = contentStr.replace(/^```\n/, '').replace(/\n```$/, '');
          }

          // Удаляем BOM и другие невидимые символы
          contentStr = contentStr.replace(/^﻿/, '').trim();

          // Исправляем неэкранированные кавычки в explanation полях
          // Используем простой и надежный подход: находим все explanation поля и исправляем кавычки
          let parsed;
          try {
            parsed = JSON.parse(contentStr);
          } catch (parseErr) {
            // Если парсинг не удался, исправляем кавычки в explanation полях
            // Позиционный подход: находим каждое explanation поле и исправляем его
            let result = '';
            let i = 0;

            while (i < contentStr.length) {
              const explanationPos = contentStr.indexOf('"explanation"', i);

              if (explanationPos === -1) {
                result += contentStr.substring(i);
                break;
              }

              result += contentStr.substring(i, explanationPos);

              // Находим начало значения
              const colonPos = contentStr.indexOf(':', explanationPos);
              const quoteStart = contentStr.indexOf('"', colonPos);

              if (quoteStart === -1) {
                result += contentStr.substring(explanationPos);
                break;
              }

              result += contentStr.substring(explanationPos, quoteStart + 1);

              // Ищем конец значения - двойную кавычку, за которой идет запятая или }
              // Проблема: в исходном JSON может не быть закрывающей двойной кавычки - строка заканчивается одинарной
              // Решение: ищем последнюю двойную кавычку перед запятой или }, или добавляем её
              let quoteEnd = -1;

              // Сначала собираем все двойные кавычки после начала значения
              const doubleQuotes = [];
              for (let j = quoteStart + 1; j < Math.min(quoteStart + 500, contentStr.length); j++) {
                if (contentStr[j] === '"' && contentStr[j - 1] !== '\\') {
                  doubleQuotes.push(j);
                }
              }

              // Ищем правильную закрывающую кавычку - идем с конца списка
              for (let idx = doubleQuotes.length - 1; idx >= 0; idx--) {
                const j = doubleQuotes[idx];
                // Пропускаем пробелы, знаки препинания и одинарные кавычки после двойной кавычки
                let k = j + 1;
                while (k < contentStr.length && (
                  /\s/.test(contentStr[k]) ||
                  contentStr[k] === '.' ||
                  contentStr[k] === '—' ||
                  contentStr[k] === '-' ||
                  contentStr[k] === ':' ||
                  contentStr[k] === "'" ||
                  contentStr[k] === '’' ||
                  contentStr[k] === '”'
                )) k++;

                // Если следующий символ - запятая или }, это конец значения
                if (k < contentStr.length && (contentStr[k] === ',' || contentStr[k] === '}')) {
                  quoteEnd = j;
                  break;
                }

                // Проверяем, не начало ли это нового поля
                if (contentStr[k] === '"') {
                  const nextText = contentStr.substring(k, Math.min(k + 30, contentStr.length));
                  if (nextText.match(/^"[a-z]+"\s*:/)) {
                    quoteEnd = j;
                    break;
                  }
                }
              }

              if (quoteEnd > quoteStart) {
                const value = contentStr.substring(quoteStart + 1, quoteEnd);
                // Экранируем все двойные кавычки внутри значения
                // Важно: сначала убираем уже экранированные, потом экранируем все
                // eslint-disable-next-line no-control-regex
                const escaped = value.replace(/\\"/g, ' ').replace(/"/g, '\\"').replace(/ /g, '\\"');
                result += escaped;
                result += '"';
                i = quoteEnd + 1;
              } else {
                // Не нашли закрывающую двойную кавычку - значит в исходном JSON её нет
                // Ищем запятую или } после одинарной кавычки или другого символа
                let valueEnd = -1;
                for (let j = quoteStart + 1; j < Math.min(quoteStart + 500, contentStr.length); j++) {
                  let k = j;
                  // Пропускаем пробелы, знаки препинания и одинарные кавычки
                  while (k < contentStr.length && (
                    /\s/.test(contentStr[k]) ||
                    contentStr[k] === '.' ||
                    contentStr[k] === '—' ||
                    contentStr[k] === '-' ||
                    contentStr[k] === ':' ||
                    contentStr[k] === "'" ||
                    contentStr[k] === '’' ||
                    contentStr[k] === '”'
                  )) k++;

                  if (k < contentStr.length && (contentStr[k] === ',' || contentStr[k] === '}')) {
                    valueEnd = j - 1; // Конец значения перед пробелами/знаками препинания
                    break;
                  }
                }

                if (valueEnd > quoteStart) {
                  const value = contentStr.substring(quoteStart + 1, valueEnd + 1);
                  // Экранируем все двойные кавычки внутри значения
                  // eslint-disable-next-line no-control-regex
                  const escaped = value.replace(/\\"/g, ' ').replace(/"/g, '\\"').replace(/ /g, '\\"');
                  result += escaped;
                  result += '"'; // Добавляем закрывающую двойную кавычку
                  i = valueEnd + 1;
                } else {
                  // Последняя попытка: добавляем остаток как есть
                  result += contentStr.substring(quoteStart + 1);
                  break;
                }
              }
            }

            try {
              parsed = JSON.parse(result);
            } catch (secondErr) {

              // Fallback: попросить OpenAI исправить JSON (как у ABCD)
              try {
                const reformatPrompt = `Convert the following text into VALID JSON strictly matching this schema:

{
  "title": "True/False Exercise",
  "level": "${trueFalseLevel}",
  "statements": [
    {
      "statement": "string",
      "isTrue": boolean,
      "explanation": "string (escape all quotes with \\")"
    }
  ]
}

Rules:
- Return ONLY valid JSON (no markdown fences, no explanations).
- Escape all double quotes inside string values with backslash (\\").
- Ensure the "statements" array contains exactly ${trueFalseCount} items.
- If the content includes extra commentary, ignore it.

Content to convert:\n\n${contentStr}`;

                let fixedText = await fetchOpenAIText({
                  prompt: reformatPrompt,
                  temperature: 0.2,
                  maxTokens: 1500
                });

                fixedText = (fixedText || '').trim();
                if (fixedText.startsWith('```json')) {
                  fixedText = fixedText.replace(/^```json[\r\n]+/, '').replace(/```$/, '').trim();
                } else if (fixedText.startsWith('```')) {
                  fixedText = fixedText.replace(/^```[\r\n]+/, '').replace(/```$/, '').trim();
                }

                const match = fixedText.match(/\{[\s\S]*\}/);
                if (match) {
                  fixedText = match[0];
                  parsed = JSON.parse(fixedText);
                } else {
                  throw new Error('No JSON found in reformatted response');
                }
              } catch (reformatErr) {
                throw parseErr;
              }
            }
          }

          if (parsed && parsed.statements && Array.isArray(parsed.statements)) {
            // Normalize structure: ensure keys { statement, isTrue, explanation }
            const normalized = {
              ...parsed,
              statements: parsed.statements.map(s => ({
                statement: s.statement || s.text || '',
                isTrue: !!s.isTrue,
                explanation: (s.explanation || '').replace(/\\"/g, '"') // Убираем экранирование для отображения
              }))
            };
            setGeneratedTrueFalse(normalized);
            // Убеждаемся, что генератор остается открытым
            setShowTrueFalseGenerator(true);

          } else {
            setGeneratedTrueFalse(null);
          }
        } catch (parseError) {
          // Сбрасываем состояние, чтобы пользователь мог попробовать снова
          setGeneratedTrueFalse(null);
        }
      } else {
        setGeneratedTrueFalse(null);
      }
    } catch (e) {
      setGeneratedTrueFalse(null);
    } finally {
      setTrueFalseLoading(false);
    }
  };

  const handleTrueFalseInsert = () => {
    if (!generatedTrueFalse) return;
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(generatedTrueFalse, 'truefalse');
      setShowTrueFalseGenerator(false);
      setTrueFalseInput("");
      setTrueFalseCount(5);
      setGeneratedTrueFalse(null);
      setSelectedTemplate(null);
      return;
    }
    if (!activeEditor) return;
    activeEditor.chain().focus().insertTrueFalseExercise({
      statements: generatedTrueFalse.statements,
      title: generatedTrueFalse.title,
      level: generatedTrueFalse.level
    }).run();

    setShowTrueFalseGenerator(false);
    setTrueFalseInput("");
    setTrueFalseCount(5);
    setGeneratedTrueFalse(null);
    setSelectedTemplate(null);
  };

  const handleTrueFalseCancel = () => {
    setShowTrueFalseGenerator(false);
    setTrueFalseInput("");
    setTrueFalseCount(5);
    setGeneratedTrueFalse(null);
    setSelectedTemplate(null);
  };

  return { handleTrueFalseGenerate, handleTrueFalseInsert, handleTrueFalseCancel };
}
